import './audio-player.component.scss';

import type MuxPlayerElement from '@mux/mux-player';
import type { FunctionComponent } from 'preact';
import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useStore } from 'react-redux';

import {
  selectAccountSettings,
  selectAccountSettingsLoaded,
  selectLoggedIn,
} from '../../../auth/store/auth.selectors';
import { loadAccountSettings } from '../../../auth/store/auth.thunk';
import { IconExplicit } from '../../../common/icons/icon-explicit';
import { IconPlayerNext } from '../../../common/icons/icon-player-next';
import { IconPlayerPause } from '../../../common/icons/icon-player-pause';
import { IconPlayerPlay } from '../../../common/icons/icon-player-play';
import { IconPlayerPrev } from '../../../common/icons/icon-player-prev';
import { ContentService } from '../../../content/content.service';
import { ContentType } from '../../../content/models/content-type.enum';
import { useTranslation } from '../../../i18n';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { selectUser } from '../../../perks/store/perks.selectors';
import { loadUser } from '../../../perks/store/perks.thunk';
import type { RootState } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { buttonize, linkify } from '../../../utils/a11y.utils';
import { getTrackId, isTrackPlayable } from '../../models/album.helpers';
import type { Track } from '../../models/album.interface';
import type { AudioPlayerProps } from '../../models/audio-player.interface';
import { getAudioEngine } from '../../services/audio-engine';
import {
  setCurrentTrackIndex,
  setMusicIsPlaying,
  updatePostQueueToken,
} from '../../store/music.reducer';
import {
  selectClientIp,
  selectCurrentTrackIndex,
  selectMusicIsPlaying,
  selectMusicIsShuffling,
  selectMusicSeekToStartAt,
} from '../../store/music.selectors';
import { loadAlbum } from '../../store/music.thunk';
import { ArtLightbox } from '../art-lightbox/art-lightbox.component';

const contentService = new ContentService();

function findPlayableIndex(tracks: Track[], from: number, dir: 1 | -1): number {
  for (let i = from; i >= 0 && i < tracks.length; i += dir) {
    if (isTrackPlayable(tracks[i])) return i;
  }
  return -1;
}

function getRandomPlayableIndex(tracks: Track[], exclude: number): number {
  const candidates = tracks
    .map((_, i) => i)
    .filter((i) => i !== exclude && isTrackPlayable(tracks[i]));
  if (!candidates.length) return exclude;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

// Times of an hour or more show H:MM:SS (-2:29:28) instead of -149:28.
function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  if (seconds < 3600) return `${Math.floor(seconds / 60)}:${s}`;
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${Math.floor(seconds / 3600)}:${m}:${s}`;
}

function isLightBackground(bgColor: string): boolean {
  const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) return false;
  const r = +match[1],
    g = +match[2],
    b = +match[3];
  const a = match[4] !== undefined ? +match[4] : 1;
  const blend = (c: number) => a * c + (1 - a) * 255;
  const luminance = (0.299 * blend(r) + 0.587 * blend(g) + 0.114 * blend(b)) / 255;
  return luminance > 0.99;
}

function getFixedBottomOffset(excludeEl: Element | null): number {
  const xs = [window.innerWidth * 0.25, window.innerWidth * 0.5, window.innerWidth * 0.75];
  let maxHeight = 0;
  for (const x of xs) {
    const els = document.elementsFromPoint(x, window.innerHeight - 1);
    for (const el of els) {
      if (excludeEl && (el === excludeEl || excludeEl.contains(el))) continue;
      const style = window.getComputedStyle(el);
      if (style.position === 'fixed') {
        const rect = el.getBoundingClientRect();
        if (window.innerHeight - rect.bottom <= 5 && rect.height > 30) {
          maxHeight = Math.max(maxHeight, rect.height);
        }
        break;
      }
    }
  }
  return maxHeight;
}

function adjustLayout(barEl: HTMLDivElement | null = null) {
  if (!barEl) return;
  const isMobile = window.innerWidth <= 750;
  const fixedOffset = isMobile ? getFixedBottomOffset(barEl) : 0;
  barEl.style.bottom = isMobile && fixedOffset > 0 ? `${fixedOffset + 15}px` : '';
}

const AudioPlayerContent: FunctionComponent<AudioPlayerProps> = ({
  tracks = [],
  portalTarget,
  colorSetting,
  colorSchemeClass,
  barBackgroundColor,
  borderWidth,
  borderRadius,
  borderColor,
  textColor,
  buttonColor,
  buttonIconColor,
  secondaryButtonColor,
  secondaryButtonIconColor,
  progressBarColor,
  progressBackgroundColor,
  showTimeElapsed = true,
  showArtwork = true,
  showPlayer = true,
  onNavigateToAlbum,
  refreshTracks,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const storeInstance = useStore();

  // Redux state
  const currentIndex = useAppSelector(selectCurrentTrackIndex);
  const isPlaying = useAppSelector(selectMusicIsPlaying);
  const isShuffling = useAppSelector(selectMusicIsShuffling);
  const seekToStartAt = useAppSelector(selectMusicSeekToStartAt);
  const clientIp = useAppSelector(selectClientIp);

  // Local state
  const [duration, setDuration] = useState(0);
  const [lightBg, setLightBg] = useState(false);
  const [showRemaining, setShowRemaining] = useState(false);
  const [hoverTime, setHoverTime] = useState(0);
  const [titleScrolling, setTitleScrolling] = useState(false);
  const [artistScrolling, setArtistScrolling] = useState(false);
  const [expandedTitleScrolling, setExpandedTitleScrolling] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const muxRef = useRef<MuxPlayerElement | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const progressWrapRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const artistRef = useRef<HTMLDivElement>(null);
  const seekbarRef = useRef<HTMLInputElement>(null);
  const currentTimeRef = useRef(0);
  const thumbPctRef = useRef(0);
  const currentTimeDisplayRef = useRef<HTMLSpanElement>(null);
  const durationDisplayRef = useRef<HTMLSpanElement>(null);
  const mobileFillRef = useRef<HTMLDivElement>(null);
  const mobileProgressRef = useRef<HTMLDivElement>(null);
  const showRemainingRef = useRef(false);
  const expandedPanelRef = useRef<HTMLDivElement>(null);
  const expandedProgressWrapRef = useRef<HTMLDivElement>(null);
  const expandedSeekbarRef = useRef<HTMLInputElement>(null);
  const expandedTimeRef = useRef<HTMLSpanElement>(null);
  const expandedRemainingRef = useRef<HTMLSpanElement>(null);
  const expandedTitleRef = useRef<HTMLDivElement>(null);
  const expandedTitleTextRef = useRef<HTMLDivElement>(null);
  const dragStartYRef = useRef<number | null>(null);
  const dragDeltaRef = useRef(0);
  const panelAnchorRef = useRef<{ bottom: number; left: number; right: number } | null>(null);
  const panelGapRef = useRef<number | null>(null);

  // Stable refs for use inside closures
  const isPlayingRef = useRef(false);
  const tracksRef = useRef(tracks);
  const currentIndexRef = useRef(currentIndex);
  const isShufflingRef = useRef(isShuffling);
  const transitioningRef = useRef(false);
  const tokenRefreshAttemptedRef = useRef(false);
  const lastPrevTapRef = useRef(0);
  const refreshTracksRef = useRef(refreshTracks);

  isPlayingRef.current = isPlaying;
  tracksRef.current = tracks;
  currentIndexRef.current = currentIndex;
  isShufflingRef.current = isShuffling;
  showRemainingRef.current = showRemaining;
  refreshTracksRef.current = refreshTracks;

  // The singleton engine outlives this component (it survives theme section
  // re-renders); this component is just the controller attached to it.
  const engine = useMemo(() => getAudioEngine(), []);
  muxRef.current = engine.el;
  const barOwnsRef = useRef(true);

  const accountSettingsLoaded = useAppSelector(selectAccountSettingsLoaded);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const loggedIn = useAppSelector(selectLoggedIn);
  const user = useAppSelector(selectUser);

  const track = tracks[currentIndex] ?? null;
  // A single-track queue (post audio, one-track album) has nothing to skip
  // to — prev/next restart the track instead of being disabled (SNGL-7299)
  const singleTrackQueue = tracks.length < 2;
  const currentAlbumId = track?.albumId ?? null;

  // Engine already playing this mount's current track (audio survived a
  // section re-render) — the first track effect must not reset it to zero.
  const [adoptedAtMount] = useState(() => {
    const pid = engine.currentPlaybackId;
    return (
      !!pid && pid === track?.playbackId && (!engine.el.paused || (engine.el.currentTime ?? 0) > 0)
    );
  });
  const adoptionPendingRef = useRef(adoptedAtMount);

  // ── Marquee ───────────────────────────────────────────────────────────────

  const setupExpandedMarquee = () => {
    const wrapEl = expandedTitleRef.current;
    const textEl = expandedTitleTextRef.current;
    if (!wrapEl || !textEl) return;

    setExpandedTitleScrolling(false);
    textEl.style.removeProperty('--scroll-distance');
    textEl.style.removeProperty('--scroll-duration');

    const overflow = Math.max(0, textEl.scrollWidth - wrapEl.clientWidth);
    if (overflow <= 0) return;

    textEl.style.setProperty('--scroll-distance', `-${overflow}px`);
    textEl.style.setProperty('--scroll-duration', `${overflow / 14 + 8}s`);
    setExpandedTitleScrolling(true);
  };

  const setupMarquee = () => {
    setupExpandedMarquee();

    const titleEl = titleRef.current;
    const artistEl = artistRef.current;
    if (!titleEl || !titleEl.style || !artistEl || !artistEl.style) return;

    setTitleScrolling(false);
    setArtistScrolling(false);
    titleEl.style.removeProperty('--scroll-distance');
    titleEl.style.removeProperty('--scroll-duration');
    artistEl.style.removeProperty('--scroll-distance');
    artistEl.style.removeProperty('--scroll-duration');

    const titleOverflow = Math.max(0, titleEl.scrollWidth - titleEl.offsetWidth);
    const artistOverflow = Math.max(0, artistEl.scrollWidth - artistEl.offsetWidth);
    const maxOverflow = Math.max(titleOverflow, artistOverflow);
    if (maxOverflow <= 0) return;

    const dur = `${maxOverflow / 14 + 8}s`;

    if (titleOverflow > 0) {
      titleEl.style.setProperty('--scroll-distance', `-${titleOverflow}px`);
      titleEl.style.setProperty('--scroll-duration', dur);
      setTitleScrolling(true);
    }
    if (artistOverflow > 0) {
      artistEl.style.setProperty('--scroll-distance', `-${artistOverflow}px`);
      artistEl.style.setProperty('--scroll-duration', dur);
      setArtistScrolling(true);
    }
  };

  // ── Load account settings if not already loaded ───────────────────────────

  useEffect(() => {
    if (!accountSettingsLoaded) {
      dispatch(loadAccountSettings());
    }
  }, [accountSettingsLoaded, dispatch]);

  // ── Load user for Mux analytics attribution when logged in ────────────────

  useEffect(() => {
    if (accountSettingsLoaded && loggedIn && !user) {
      dispatch(loadUser());
    }
  }, [accountSettingsLoaded, loggedIn, user, dispatch]);

  // ── Proactively fetch fresh tokens before Mux hits an error ───────────────

  useEffect(() => {
    // Hosts that pass refreshTracks own their album loading (keyed by
    // product id, not album id) — loadAlbum here would just duplicate it.
    if (refreshTracks || !currentAlbumId) return;
    const state = storeInstance.getState() as RootState;
    if (state.music.albums[currentAlbumId] || state.music.albumsLoading[currentAlbumId]) return;
    dispatch(loadAlbum(currentAlbumId));
  }, [currentAlbumId, dispatch]);

  // ── Mobile breakpoint tracker ─────────────────────────────────────────────

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Expanded player: scroll lock, collapse when leaving mobile ────────────

  useEffect(() => {
    if (!isMobile) setExpanded(false);
  }, [isMobile]);

  useEffect(() => {
    if (!expanded) {
      setLightboxOpen(false);
      return;
    }
    // The panel takes its width from the bar's anchor when it opens.
    requestAnimationFrame(() => setupExpandedMarquee());
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    // Anchor the sheet to the proxy's mobile nav when present — it sits just
    // above the menu with matching side margins. On app-block pages (no nav)
    // the mini bar's rect anchors it instead. Rects are only trusted when
    // the element is actually on screen (tab changes and tuck animations can
    // measure transformed, off-screen rects); otherwise the last good anchor
    // is reused.
    if (expandedPanelRef.current) {
      const onScreen = (rect: DOMRect | undefined): rect is DOMRect =>
        !!rect && rect.height > 0 && rect.bottom > 0 && rect.top <= window.innerHeight + 2;

      const navRect = document
        .querySelector('#single-mobile-stack .mobile-nav')
        ?.getBoundingClientRect();
      const barRect = barRef.current?.getBoundingClientRect();

      // The stack's real bar↔nav spacing, cached so reopens mid tuck-animation
      // (bar off screen) keep the same gap.
      if (onScreen(navRect) && onScreen(barRect) && barRect.bottom <= navRect.top + 2) {
        panelGapRef.current = Math.max(4, navRect.top - barRect.bottom);
      }
      const gap = panelGapRef.current ?? 8;

      if (onScreen(navRect)) {
        panelAnchorRef.current = {
          bottom: Math.max(15, window.innerHeight - navRect.top + gap),
          left: Math.max(0, navRect.left),
          right: Math.max(0, window.innerWidth - navRect.right),
        };
      } else if (onScreen(barRect) && barRect.bottom <= window.innerHeight + 2) {
        panelAnchorRef.current = {
          bottom: Math.max(15, window.innerHeight - barRect.bottom),
          left: Math.max(0, barRect.left),
          right: Math.max(0, window.innerWidth - barRect.right),
        };
      }
      const anchor = panelAnchorRef.current ?? { bottom: 15, left: 10, right: 10 };
      expandedPanelRef.current.style.bottom = `${anchor.bottom}px`;
      expandedPanelRef.current.style.left = `${anchor.left}px`;
      expandedPanelRef.current.style.right = `${anchor.right}px`;

      // Never grow up into a fixed/sticky header — cap the sheet's height to
      // the space between the header's bottom edge and the anchor.
      let clearance = 80;
      const topEls = document.elementsFromPoint(window.innerWidth / 2, 2);
      for (const el of topEls) {
        if (el === expandedPanelRef.current || expandedPanelRef.current.contains(el)) continue;
        const cs = window.getComputedStyle(el);
        if (cs.position === 'fixed' || cs.position === 'sticky') {
          clearance = Math.max(clearance, el.getBoundingClientRect().bottom + 10);
          break;
        }
      }
      expandedPanelRef.current.style.maxHeight = `${Math.max(
        320,
        window.innerHeight - anchor.bottom - clearance
      )}px`;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Navigating while expanded (nav tab tap, back button) reflows the page
    // under the sheet and can push it over the header — close instead.
    const close = () => setExpanded(false);
    const onNavClick = (e: Event) => {
      if ((e.target as HTMLElement)?.closest('#single-mobile-stack .mobile-nav')) close();
    };
    document.addEventListener('click', onNavClick, true);
    window.addEventListener('hashchange', close);
    window.addEventListener('popstate', close);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('click', onNavClick, true);
      window.removeEventListener('hashchange', close);
      window.removeEventListener('popstate', close);
    };
  }, [expanded]);

  // ── Mount: body attr, padding, resize listener ────────────────────────────

  useEffect(() => {
    document.body.setAttribute('data-audio-player', '');
    adjustLayout(barRef.current);
    const handleResize = () => {
      adjustLayout(barRef.current);
      requestAnimationFrame(() => setupMarquee());
    };
    let scrollTimer = 0;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => adjustLayout(barRef.current), 150);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
      }
    };
  }, []);

  // ── Attach to the shared engine for this component's lifetime ────────────

  useEffect(() => {
    barOwnsRef.current = engine.claimedBy == null || engine.claimedBy === 'bar';
    const offs = [
      engine.on('ownerchange', ((e: CustomEvent) => {
        const owner = e.detail?.owner as string | undefined;
        const owns = owner == null || owner === 'bar';
        const lost = barOwnsRef.current && !owns;
        barOwnsRef.current = owns;
        if (lost) dispatch(setMusicIsPlaying(false));
      }) as EventListener),
      engine.on('playing', handlePlaying),
      engine.on('pause', handlePause),
      engine.on('ended', handleEnded),
      engine.on('timeupdate', handleTimeUpdate),
      engine.on('loadedmetadata', handleLoadedMetadata),
      engine.on('error', handleMuxError),
    ];
    engine.retain();
    return () => {
      offs.forEach((off) => off());
      engine.release();
    };
  }, []);

  // ── Sync mux play/pause with Redux isPlaying ──────────────────────────────

  useEffect(() => {
    const mux = muxRef.current;
    if (!mux) return;
    if (transitioningRef.current) return;
    if (isPlaying) {
      if (!barOwnsRef.current) {
        engine.claim('bar');
        barOwnsRef.current = true;
        const track = tracksRef.current[currentIndexRef.current];
        if (track) engine.applyTrack(track);
      }
      mux.play()?.catch(() => {});
    } else if (barOwnsRef.current) {
      mux.pause();
    }
  }, [isPlaying]);

  // ── Restart current track on demand ──────────────────────────────────────

  // Seek to 0 and zero the progress UI without touching play/pause state.
  // Refs only, so the media-session handlers (registered once) can share it.
  const resetToTrackStart = useCallback(() => {
    const mux = muxRef.current;
    if (mux) mux.currentTime = 0;
    currentTimeRef.current = 0;
    thumbPctRef.current = 0;
    progressWrapRef.current?.style.setProperty('--thumb-pct', '0%');
    if (currentTimeDisplayRef.current) currentTimeDisplayRef.current.textContent = '0:00';
    if (mobileFillRef.current) mobileFillRef.current.style.width = '0%';
  }, []);

  useEffect(() => {
    if (!seekToStartAt) return;
    if (!barOwnsRef.current) return;
    const mux = muxRef.current;
    if (!mux) return;
    resetToTrackStart();
    mux.play()?.catch(() => {});
  }, [seekToStartAt]);

  // ── Mux event handlers (stable callbacks via refs) ────────────────────────

  const applyMediaSession = useCallback(() => {
    if (!('mediaSession' in navigator)) return;

    // Metadata
    const track = tracksRef.current[currentIndexRef.current] ?? null;
    if (track) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.explicit ? `${track.title || ''} \u{1F174}` : track.title || '',
        artist: track.mergedArtists || '',
        album: track.albumTitle || '',
        artwork: track.poster ? [{ src: track.poster, sizes: '960x960', type: 'image/jpeg' }] : [],
      });
    }

    // Action handlers (re-registered each time to satisfy Arc)
    navigator.mediaSession.setActionHandler('play', () => {
      dispatch(setMusicIsPlaying(true));
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      dispatch(setMusicIsPlaying(false));
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime == null || !muxRef.current) return;
      muxRef.current.currentTime = details.seekTime;
    });
    // A single-track queue (post audio) has nothing to skip to — prev/next
    // restart the track, matching the in-page buttons (SNGL-7299).
    if ((tracksRef.current?.length ?? 0) < 2) {
      navigator.mediaSession.setActionHandler('previoustrack', resetToTrackStart);
      navigator.mediaSession.setActionHandler('nexttrack', resetToTrackStart);
      return;
    }
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      const tracks = tracksRef.current;
      const idx = currentIndexRef.current;
      if (!tracks?.length) return;
      if (isShufflingRef.current) {
        dispatch(setCurrentTrackIndex(getRandomPlayableIndex(tracks, idx)));
      } else {
        const prevIdx = findPlayableIndex(tracks, idx - 1, -1);
        if (prevIdx >= 0) dispatch(setCurrentTrackIndex(prevIdx));
      }
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      const tracks = tracksRef.current;
      const idx = currentIndexRef.current;
      if (!tracks?.length) return;
      if (isShufflingRef.current) {
        dispatch(setCurrentTrackIndex(getRandomPlayableIndex(tracks, idx)));
      } else {
        const nextIdx = findPlayableIndex(tracks, idx + 1, 1);
        if (nextIdx >= 0) dispatch(setCurrentTrackIndex(nextIdx));
      }
    });
  }, []);

  const handlePlaying = useCallback(() => {
    if (!barOwnsRef.current) return;
    dispatch(setMusicIsPlaying(true));
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
    applyMediaSession();
  }, [applyMediaSession]);

  const handlePause = useCallback(() => {
    if (!barOwnsRef.current) return;
    if (transitioningRef.current) return;
    dispatch(setMusicIsPlaying(false));
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'paused';
    }
  }, []);

  const handleEnded = useCallback(() => {
    if (!barOwnsRef.current) return;
    if (!tracksRef.current?.length) return;
    const idx = currentIndexRef.current;
    const next = isShufflingRef.current
      ? getRandomPlayableIndex(tracksRef.current, idx)
      : findPlayableIndex(tracksRef.current, idx + 1, 1);
    if (next >= 0 && next !== idx) {
      transitioningRef.current = true;
      dispatch(setCurrentTrackIndex(next));
    } else {
      dispatch(setMusicIsPlaying(false));
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (!barOwnsRef.current) return;
    const mux = muxRef.current;
    if (!mux?.duration) return;
    const pct = (mux.currentTime / mux.duration) * 100;

    currentTimeRef.current = mux.currentTime;
    thumbPctRef.current = pct;

    progressWrapRef.current?.style.setProperty('--thumb-pct', `${pct}%`);
    if (seekbarRef.current) seekbarRef.current.value = String(Math.round(pct * 10));

    if (currentTimeDisplayRef.current)
      currentTimeDisplayRef.current.textContent = formatTime(mux.currentTime);

    if (durationDisplayRef.current) {
      durationDisplayRef.current.textContent =
        showRemainingRef.current && mux.duration > 0
          ? `-${formatTime(Math.max(0, mux.duration - mux.currentTime))}`
          : formatTime(mux.duration);
    }

    if (mobileFillRef.current) mobileFillRef.current.style.width = `${pct}%`;

    expandedProgressWrapRef.current?.style.setProperty('--thumb-pct', `${pct}%`);
    if (expandedSeekbarRef.current) expandedSeekbarRef.current.value = String(Math.round(pct * 10));
    if (expandedTimeRef.current) expandedTimeRef.current.textContent = formatTime(mux.currentTime);
    if (expandedRemainingRef.current)
      expandedRemainingRef.current.textContent = `-${formatTime(
        Math.max(0, mux.duration - mux.currentTime)
      )}`;

    // Album-only: the tracklist countdown listener carries no track identity,
    // so post-audio progress must not reach it.
    if (tracksRef.current[currentIndexRef.current]?.albumId) {
      window.dispatchEvent(
        new CustomEvent('audioTimeUpdate', {
          detail: { currentTime: mux.currentTime, duration: mux.duration },
        })
      );
    }

    if ('mediaSession' in navigator) {
      navigator.mediaSession.setPositionState({
        duration: mux.duration,
        playbackRate: mux.playbackRate ?? 1,
        position: mux.currentTime,
      });
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (!barOwnsRef.current) return;
    const mux = muxRef.current;
    if (mux) setDuration(mux.duration);
  }, []);

  const handleMuxError = useCallback(
    (e: Event) => {
      if (!barOwnsRef.current) return;
      console.error(e);
      if (tokenRefreshAttemptedRef.current) return;
      // App blocks load albums by product id, so they supply their own
      // refresh; the album-id path is the default for everything else.
      if (refreshTracksRef.current) {
        tokenRefreshAttemptedRef.current = true;
        refreshTracksRef.current();
        return;
      }
      const errorTrack = tracksRef.current[currentIndexRef.current];
      const albumId = errorTrack?.albumId;
      if (albumId) {
        tokenRefreshAttemptedRef.current = true;
        dispatch(loadAlbum(albumId));
        return;
      }
      // No albumId = post-audio queue entry: re-mint via the content endpoint
      const contentId = errorTrack ? getTrackId(errorTrack) : undefined;
      if (contentId) {
        tokenRefreshAttemptedRef.current = true;
        contentService
          .loadAudioPlayback(contentId)
          .then((fresh) => dispatch(updatePostQueueToken(fresh)))
          .catch(() => undefined);
      }
    },
    [dispatch]
  );

  // ── Track change: reset progress, play if needed, media session, marquee ──

  // Reset token-refresh guard only when the track index changes, not when
  // tracks data changes (which would create a dispatch → tracks update → reset loop).
  useEffect(() => {
    tokenRefreshAttemptedRef.current = false;
  }, [currentIndex]);

  useEffect(() => {
    if (!barOwnsRef.current) {
      if (!isPlayingRef.current && !transitioningRef.current) return;
      engine.claim('bar');
      barOwnsRef.current = true;
    }
    engine.applyTrack(track ?? null);

    if (adoptionPendingRef.current) {
      // Audio kept playing across a remount — sync the UI to the engine
      // instead of resetting playback to zero.
      adoptionPendingRef.current = false;
      const time = engine.el.currentTime ?? 0;
      const dur = engine.el.duration ?? 0;
      currentTimeRef.current = time;
      thumbPctRef.current = dur > 0 ? (time / dur) * 100 : 0;
      if (dur > 0) setDuration(dur);
      progressWrapRef.current?.style.setProperty('--thumb-pct', `${thumbPctRef.current}%`);
      if (currentTimeDisplayRef.current)
        currentTimeDisplayRef.current.textContent = formatTime(time);
      if (mobileFillRef.current) mobileFillRef.current.style.width = `${thumbPctRef.current}%`;
      applyMediaSession();
      requestAnimationFrame(() => requestAnimationFrame(() => setupMarquee()));
      return;
    }

    currentTimeRef.current = 0;
    thumbPctRef.current = 0;
    setDuration(0);
    progressWrapRef.current?.style.setProperty('--thumb-pct', '0%');
    if (currentTimeDisplayRef.current) currentTimeDisplayRef.current.textContent = '0:00';
    if (mobileFillRef.current) mobileFillRef.current.style.width = '0%';

    if (isPlayingRef.current || transitioningRef.current) {
      const mux = muxRef.current;
      if (mux) {
        // canplay handles a new src that needs to load
        mux.addEventListener(
          'canplay',
          () => {
            transitioningRef.current = false;
            mux.play()?.catch(() => {});
          },
          { once: true }
        );
        // seek + direct play handles same src (e.g. test data with shared playbackId)
        mux.currentTime = 0;
        mux.play()?.catch(() => {});
      }
    } else {
      transitioningRef.current = false;
    }

    applyMediaSession();

    requestAnimationFrame(() => requestAnimationFrame(() => setupMarquee()));
  }, [currentIndex, tracks]);

  // ── Mux analytics metadata (imperative — the engine element is shared) ────

  useEffect(() => {
    // A track without an albumId is a post-audio queue entry
    engine.setMetadata({
      video_id: track ? getTrackId(track) : undefined,
      video_title: track?.title,
      viewer_user_id: user?.externalId,
      custom_2: track?.mergedArtists,
      custom_3: clientIp ?? undefined,
      custom_4: track?.albumId ? ContentType.ALBUM : ContentType.AUDIO,
      custom_5: track?.albumId ?? (track ? getTrackId(track) : undefined),
      custom_6: track ? getTrackId(track) : undefined,
    });
  }, [track, user?.externalId, clientIp]);

  // ── CSS variable theming ──────────────────────────────────────────────────

  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {
      '--barBackgroundColor': barBackgroundColor ?? '',
      '--borderColor': borderColor ?? '',
      '--borderWidth': borderWidth ?? '',
      '--borderRadius': borderRadius ?? '',
      '--textColor': textColor ?? '',
      '--buttonColor': buttonColor ?? '',
      '--buttonIconColor': buttonIconColor ?? '',
      '--secondaryButtonColor': secondaryButtonColor ?? '',
      '--secondaryButtonIconColor': secondaryButtonIconColor ?? '',
      '--progressBarColor': progressBarColor ?? '',
      '--progressBackgroundColor': progressBackgroundColor ?? '',
    };

    if (accountSettings?.cardBackgroundColor)
      vars['--single-settings-bg-color'] = accountSettings.cardBackgroundColor;
    if (accountSettings?.cardBorderColor)
      vars['--single-settings-brdr-color'] = accountSettings.cardBorderColor;
    if (accountSettings?.cardTextColor)
      vars['--single-settings-text-color'] = accountSettings.cardTextColor;
    if (accountSettings?.cardButtonBackgroundColor)
      vars['--single-settings-button-bg-color'] = accountSettings.cardButtonBackgroundColor;
    if (accountSettings?.cardButtonTextColor)
      vars['--single-settings-button-text-color'] = accountSettings.cardButtonTextColor;
    if (accountSettings?.secondaryButtonBackgroundColor)
      vars['--single-settings-secondary-button-bg-color'] =
        accountSettings.secondaryButtonBackgroundColor;
    if (accountSettings?.secondaryButtonTextColor)
      vars['--single-settings-secondary-button-text-color'] =
        accountSettings.secondaryButtonTextColor;
    if (accountSettings?.borderWidth)
      vars['--single-settings-border-width'] = `${accountSettings.borderWidth}px`;
    if (accountSettings?.borderRadius)
      vars['--single-settings-border-radius'] = `${accountSettings.borderRadius}px`;

    return vars;
  }, [
    barBackgroundColor,
    borderWidth,
    borderRadius,
    borderColor,
    textColor,
    buttonColor,
    buttonIconColor,
    secondaryButtonColor,
    secondaryButtonIconColor,
    progressBarColor,
    progressBackgroundColor,
    accountSettings?.cardBackgroundColor,
    accountSettings?.cardBorderColor,
    accountSettings?.cardTextColor,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
    accountSettings?.secondaryButtonBackgroundColor,
    accountSettings?.secondaryButtonTextColor,
    accountSettings?.borderWidth,
    accountSettings?.borderRadius,
  ]);

  useEffect(() => {
    if (!barRef.current) return;
    const bg = getComputedStyle(barRef.current).backgroundColor;
    setLightBg(isLightBackground(bg));
  }, [cssVars]);

  // ── Duration display: initial value + showRemaining toggle ───────────────

  useEffect(() => {
    if (!durationDisplayRef.current) return;
    if (showRemaining && duration > 0) {
      durationDisplayRef.current.textContent = `-${formatTime(
        Math.max(0, duration - currentTimeRef.current)
      )}`;
    } else {
      durationDisplayRef.current.textContent = formatTime(duration);
    }
  }, [showRemaining, duration]);

  // ── Controls ──────────────────────────────────────────────────────────────

  const togglePlay = () => {
    dispatch(setMusicIsPlaying(!isPlayingRef.current));
  };

  const stopAndReset = () => {
    transitioningRef.current = false;
    muxRef.current?.pause();
    resetToTrackStart();
    dispatch(setMusicIsPlaying(false));
    dispatch(setCurrentTrackIndex(Math.max(0, findPlayableIndex(tracks, 0, 1))));
  };

  const prev = () => {
    if (!tracks?.length) return;
    if (singleTrackQueue) {
      resetToTrackStart();
      return;
    }
    if (isShuffling) {
      const idx = getRandomPlayableIndex(tracks, currentIndex);
      if (idx !== currentIndex) dispatch(setCurrentTrackIndex(idx));
      return;
    }

    const now = Date.now();
    const isDoubleTap = now - lastPrevTapRef.current < 500;
    lastPrevTapRef.current = now;

    if (isDoubleTap || currentIndex === 0) {
      // Double-tap or already at first track: go to previous track
      const prevIdx = findPlayableIndex(tracks, currentIndex - 1, -1);
      if (prevIdx >= 0) {
        dispatch(setCurrentTrackIndex(prevIdx));
      } else {
        stopAndReset();
      }
    } else {
      // Single tap: restart current song
      resetToTrackStart();
    }
  };

  const next = () => {
    if (!tracks?.length) return;
    if (singleTrackQueue) {
      resetToTrackStart();
      return;
    }
    if (isShuffling) {
      const idx = getRandomPlayableIndex(tracks, currentIndex);
      if (idx !== currentIndex) dispatch(setCurrentTrackIndex(idx));
      return;
    }
    const nextIdx = findPlayableIndex(tracks, currentIndex + 1, 1);
    if (nextIdx >= 0) {
      dispatch(setCurrentTrackIndex(nextIdx));
    } else {
      stopAndReset();
    }
  };

  // ── Progress interactions ─────────────────────────────────────────────────

  // Touch has no hover: while a finger (or mouse) is down on a seekbar the wrapper grows and
  // shows the thumb, and the fill follows the drag live — see the .seeking styles (SNGL-7207)
  const setSeeking = (ref: { current: HTMLElement | null }, on: boolean) => {
    ref.current?.classList.toggle('seeking', on);
  };

  // For the range inputs the release is caught on window: a native slider drag can end off
  // the element, so a pointerup handler on the input itself leaves the class stuck on
  const beginSeeking = (ref: { current: HTMLElement | null }) => {
    setSeeking(ref, true);
    const end = () => {
      setSeeking(ref, false);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  const handleMouseMove = (e: MouseEvent) => {
    const wrap = progressWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverTime(muxRef.current?.duration ? pct * muxRef.current.duration : 0);
    wrap.style.setProperty('--hover-pct', `${pct * 100}%`);
    const tooltip = wrap.querySelector('.single-ap-seek-tooltip') as HTMLElement | null;
    if (tooltip) tooltip.style.left = `${pct * 100}%`;
  };

  const handleSeek = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    if (!muxRef.current?.duration) return;
    const pct = input.valueAsNumber / 1000;
    muxRef.current.currentTime = pct * muxRef.current.duration;
    currentTimeRef.current = muxRef.current.currentTime;
    thumbPctRef.current = pct * 100;
    progressWrapRef.current?.style.setProperty('--thumb-pct', `${pct * 100}%`);
    expandedProgressWrapRef.current?.style.setProperty('--thumb-pct', `${pct * 100}%`);
  };

  // ── Expanded player (mobile) ──────────────────────────────────────────────

  const expandPlayer = () => {
    if (!isMobile) return;
    setExpanded(true);
  };

  const handleArtClick = () => {
    if (isMobile) {
      expandPlayer();
      return;
    }
    if (track?.albumId && onNavigateToAlbum) {
      onNavigateToAlbum(`/music/${track.albumId}`);
    }
  };

  // Swipe down anywhere above the controls closes the expanded player.
  // The panel follows the finger (inline transform, no re-renders); releasing
  // past the threshold collapses, otherwise the class transition snaps back.
  // Attached imperatively: iOS only honors preventDefault on non-passive
  // touchmove listeners, and JSX handlers can't opt out of passive.
  useEffect(() => {
    const el = expandedPanelRef.current;
    if (!el) return;

    const start = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.single-ap-controls') || target.closest('.single-ap-expanded-seekbar')) {
        return;
      }
      dragStartYRef.current = e.touches[0].clientY;
      dragDeltaRef.current = 0;
      if (expandedPanelRef.current) expandedPanelRef.current.style.transition = 'none';
    };
    const move = (e: TouchEvent) => {
      if (dragStartYRef.current == null) return;
      const dy = Math.max(0, e.touches[0].clientY - dragStartYRef.current);
      dragDeltaRef.current = dy;
      // Dead zone before claiming the gesture: a preventDefault'd touchmove
      // suppresses the synthetic click on iOS, which would swallow taps
      // (art lightbox) that jitter by a pixel or two.
      if (dy < 8) return;
      e.preventDefault();
      if (expandedPanelRef.current)
        expandedPanelRef.current.style.transform = `translateY(${dy}px)`;
    };
    const end = () => {
      dragStartYRef.current = null;
      const panel = expandedPanelRef.current;
      if (panel) {
        panel.style.transition = '';
        panel.style.transform = '';
      }
      if (dragDeltaRef.current > 80) setExpanded(false);
      dragDeltaRef.current = 0;
    };

    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, [isMobile]);

  const handleMobileSeek = (e: TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    if (!touch) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    if (muxRef.current?.duration) {
      muxRef.current.currentTime = pct * muxRef.current.duration;
      currentTimeRef.current = muxRef.current.currentTime;
    }
    thumbPctRef.current = pct * 100;
    // Paint the drag position now — waiting for timeupdate gives the finger no feedback
    if (mobileFillRef.current) mobileFillRef.current.style.width = `${pct * 100}%`;
  };

  const handleMobileSeekStart = (e: TouchEvent) => {
    setSeeking(mobileProgressRef, true);
    handleMobileSeek(e);
  };

  const handleMobileSeekEnd = () => setSeeking(mobileProgressRef, false);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {createPortal(
        <div
          class={`single-ap-bar${lightBg ? ' no-text-shadow' : ''}${colorSetting === 'color_scheme_type' ? ' single-color-scheme-type' : ''}${colorSchemeClass ? ` ${colorSchemeClass}` : ''}${expanded ? ' single-ap-bar--tucked' : ''}`}
          ref={barRef}
          style={{ ...cssVars, ...(showPlayer ? {} : { display: 'none' }) }}
        >
          <div class="single-ap-main">
            <div class="single-ap-info">
              {showArtwork &&
                (isMobile || (track?.albumId && onNavigateToAlbum) ? (
                  <div
                    class="single-ap-art single-ap-art-link"
                    {...linkify(handleArtClick, track?.albumTitle ?? track?.title ?? 'Album')}
                  >
                    {track?.poster && <img src={track.poster} alt="" />}
                  </div>
                ) : (
                  <div class="single-ap-art">
                    {track?.poster && <img src={track.poster} alt="" />}
                  </div>
                ))}
              <div class="single-ap-meta">
                <div class={`single-ap-title${titleScrolling ? ' scrolling' : ''}`} ref={titleRef}>
                  {track?.postId && onNavigateToAlbum ? (
                    <span
                      class="single-ap-title-link"
                      {...linkify(() => onNavigateToAlbum(`/posts/${track.postId}`))}
                    >
                      {track?.title ?? ''}
                    </span>
                  ) : track?.albumId && onNavigateToAlbum ? (
                    <span
                      class="single-ap-title-link"
                      {...linkify(() => onNavigateToAlbum(`/music/${track.albumId}`))}
                    >
                      {track?.title ?? ''}
                    </span>
                  ) : (
                    <span>{track?.title ?? ''}</span>
                  )}
                </div>
                <div
                  class={`single-ap-artist${artistScrolling ? ' scrolling' : ''}`}
                  ref={artistRef}
                >
                  {track?.explicit && <IconExplicit width={12} height={12} />}
                  <span>{track?.mergedArtists ?? ''}</span>
                </div>
              </div>
            </div>

            <div class="single-ap-progress">
              {showTimeElapsed && (
                <span class="single-ap-time single-ap-current-time" ref={currentTimeDisplayRef} />
              )}
              <div
                class="single-ap-progress-fill-wrapper"
                ref={progressWrapRef}
                onMouseMove={handleMouseMove}
              >
                <div class="single-ap-progress-fill" />
                <div class="single-ap-seek-thumb" />
                <div class="single-ap-seek-tooltip">{formatTime(hoverTime)}</div>
                <input
                  type="range"
                  class="single-ap-seekbar"
                  aria-label={t('a11y.seek')}
                  ref={seekbarRef}
                  min="0"
                  max="1000"
                  defaultValue={0}
                  onInput={handleSeek}
                  onPointerDown={() => beginSeeking(progressWrapRef)}
                />
              </div>
              {showTimeElapsed && (
                <span
                  class={`single-ap-time single-ap-duration${showRemaining ? ' remaining' : ''}`}
                  ref={durationDisplayRef}
                  {...buttonize(() => setShowRemaining((v) => !v), 'Toggle remaining time')}
                />
              )}
            </div>

            <div class="single-ap-controls">
              <button
                class="single-ap-btn single-ap-prev"
                aria-label={t('a11y.previousTrack')}
                onClick={prev}
              >
                <IconPlayerPrev />
              </button>
              <button
                class="single-ap-btn single-ap-play-pause"
                aria-label={isPlaying ? t('a11y.pause') : t('a11y.play')}
                onClick={togglePlay}
              >
                {isPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}
              </button>
              <button
                class="single-ap-btn single-ap-next"
                aria-label={t('a11y.nextTrack')}
                onClick={next}
              >
                <IconPlayerNext />
              </button>
            </div>
          </div>

          <div
            class="single-ap-mobile-progress"
            ref={mobileProgressRef}
            onTouchStart={handleMobileSeekStart}
            onTouchMove={handleMobileSeek}
            onTouchEnd={handleMobileSeekEnd}
            onTouchCancel={handleMobileSeekEnd}
          >
            <div class="single-ap-mobile-fill" ref={mobileFillRef} />
          </div>
        </div>,
        isMobile && portalTarget ? portalTarget : document.body
      )}
      {isMobile &&
        createPortal(
          <div
            class={`single-ap-expanded${expanded ? ' open' : ''}${lightBg ? ' no-text-shadow' : ''}${colorSetting === 'color_scheme_type' ? ' single-color-scheme-type' : ''}${colorSchemeClass ? ` ${colorSchemeClass}` : ''}`}
            ref={expandedPanelRef}
            style={cssVars}
            aria-hidden={!expanded}
          >
            <div
              class="single-ap-expanded-handle"
              {...buttonize(() => setExpanded(false), 'Collapse player')}
            >
              <span />
            </div>
            <div
              class="single-ap-expanded-art"
              {...buttonize(() => setLightboxOpen(true), 'View artwork')}
            >
              {track?.poster && <img src={track.poster.replace(/\/MEDIUM$/, '/FULL')} alt="" />}
            </div>
            <div class="single-ap-expanded-meta">
              <div
                class={`single-ap-expanded-title${expandedTitleScrolling ? ' scrolling' : ''}`}
                ref={expandedTitleRef}
              >
                <div class="single-ap-expanded-title-text" ref={expandedTitleTextRef}>
                  {track?.albumId && onNavigateToAlbum ? (
                    <span
                      class="single-ap-title-link"
                      {...linkify(() => {
                        setExpanded(false);
                        onNavigateToAlbum(`/music/${track.albumId}`);
                      })}
                    >
                      {track?.title ?? ''}
                    </span>
                  ) : (
                    <span>{track?.title ?? ''}</span>
                  )}
                  {track?.explicit && <IconExplicit width={14} height={14} />}
                </div>
              </div>
              <div class="single-ap-expanded-artist">{track?.mergedArtists ?? ''}</div>
            </div>
            <div class="single-ap-expanded-progress" ref={expandedProgressWrapRef}>
              <div class="single-ap-expanded-track">
                <div class="single-ap-expanded-fill" />
                <div class="single-ap-expanded-thumb" />
                <input
                  type="range"
                  class="single-ap-expanded-seekbar"
                  aria-label={t('a11y.seek')}
                  ref={expandedSeekbarRef}
                  min="0"
                  max="1000"
                  defaultValue={0}
                  onInput={handleSeek}
                  onPointerDown={() => beginSeeking(expandedProgressWrapRef)}
                />
              </div>
              <div class="single-ap-expanded-times">
                <span ref={expandedTimeRef}>0:00</span>
                <span ref={expandedRemainingRef} />
              </div>
            </div>
            <div class="single-ap-controls">
              <button
                class="single-ap-btn single-ap-prev"
                aria-label={t('a11y.previousTrack')}
                onClick={prev}
              >
                <IconPlayerPrev />
              </button>
              <button
                class="single-ap-btn single-ap-play-pause"
                aria-label={isPlaying ? t('a11y.pause') : t('a11y.play')}
                onClick={togglePlay}
              >
                {isPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}
              </button>
              <button
                class="single-ap-btn single-ap-next"
                aria-label={t('a11y.nextTrack')}
                onClick={next}
              >
                <IconPlayerNext />
              </button>
            </div>
          </div>,
          document.body
        )}
      {isMobile && lightboxOpen && track?.poster && (
        <ArtLightbox
          images={[{ url: track.poster.replace(/\/MEDIUM$/, '/FULL'), label: track.albumTitle }]}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export const AudioPlayerComponent: FunctionComponent<AudioPlayerProps> = (props) => {
  return <AudioPlayerContent {...props} />;
};
